<template>
  <div v-loading="loading">
    <div class="my-4 space-y-2 bg-brand-sand rounded-md shadow-sm px-4 py-4">
      <p class="text-brand-black font-medium">
        Please use
        <a href="https://docs.churnkey.co/chargebee" target="_blank" class="underline text-brand-blue font-semibold">
          our guide for connecting your Chargebee account </a
        >.
      </p>
      <p class="text-brand-brown text-sm font-medium">
        Your account specific webhook URL (for step 2):
        <span class="text-brand-blue font-semibold"> https://billing.churnkey.co/e/src_JDSQXrbmQU2j?appId={{ $store.state.org.appId }} </span>
      </p>
    </div>
    <div class="space-y-6 divide-y-2 divide-brand-sand">
      <div>
        <div class="header flex items-center space-x-2">
          <p v-if="versionError" class="text-lg font-bold text-brand-red flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'times-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <span> Product Catalog </span>
        </div>
        <div class="grid gap-4 max-w-xl mt-2">
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">Version</p>
            <div v-if="!editVersion">
              <el-input :value="version.label" type="text" disabled class="w-auto"></el-input>
            </div>
            <div v-if="editVersion">
              <el-select v-model="version" placeholder="Select">
                <el-option v-for="item in versionOptions" :key="item.value" :label="item.label" :value="item"> </el-option>
              </el-select>
            </div>
            <div v-if="versionError && versionError === version.value" class="py-2 text-xs font-semibold leading-5 text-red-700">
              <div>We detected that you might have selected the wrong Product Catalog version.</div>
            </div>
          </div>
        </div>
        <div class="mt-4">
          <el-button v-if="!editVersion" @click="editVersion = true" type="primary">Edit Version</el-button>
          <el-button v-if="!editVersion && versionError" @click="switchVersion()" type="danger">Switch to Version {{ otherVersion }}</el-button>
          <el-button v-if="editVersion" @click="editVersion = false" type="text">Cancel</el-button>
          <el-button v-if="editVersion" @click="saveVersion" type="primary">Save</el-button>
        </div>
      </div>
      <div class="pt-4">
        <div class="header flex items-center space-x-2">
          <p v-if="liveBadCreds" class="text-lg font-bold text-brand-red flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'times-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <p v-else-if="liveValidated" class="text-lg font-bold text-green-700 flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'check-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <p v-else class="text-lg font-bold text-green-700 flex items-center space-x-2">
            <font-awesome-layers class="text-xl">
              <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-medium-blue" />
              <font-awesome-icon :icon="['fas', 'plug']" class="text-xs text-brand-blue" :class="'text-brand-blue'" />
            </font-awesome-layers>
          </p>
          <span> Live Mode </span>
        </div>
        <div class="grid gap-4 max-w-xl mt-2">
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">Site name</p>
            <el-input v-if="!editLive && liveCredentials.site" :value="liveCredentials.site" type="text" disabled>
              <template slot="prepend">https://</template>
              <template slot="append">.chargebee.com</template>
            </el-input>
            <el-input v-else v-model="liveSite" type="text" placeholder="your-company">
              <template slot="prepend">https://</template>
              <template slot="append">.chargebee.com</template>
            </el-input>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">API Key</p>
            <el-input v-if="!editLive && liveCredentials.apiKey" :value="liveCredentials.apiKey.slice(0, 7) + '...'" type="text" disabled></el-input>
            <el-input v-else v-model="liveApiKey" type="text" placeholder="live_YOUR_API_KEY"></el-input>
          </div>
        </div>
        <div class="mt-4">
          <el-button v-if="hasAllLiveCredentials && !editLive" @click="editLive = true" type="primary">Edit Live Credentials</el-button>
          <el-button v-if="editLive" @click="editLive = false" type="text">Cancel</el-button>
          <el-button v-if="!hasAllLiveCredentials || editLive" @click="saveLiveCredentials" type="primary">Save</el-button>
          <el-button v-if="!editLive && hasAllLiveCredentials" @click="checkLiveMode" :loading="isLoadingLive" type="default"
            >Verify Connection</el-button
          >
        </div>
      </div>

      <div class="pt-4">
        <div class="header flex items-center space-x-2">
          <p v-if="testBadCreds" class="text-lg font-bold text-brand-red flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'times-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <p v-else-if="testValidated" class="text-lg font-bold text-green-700 flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'check-circle']" class="text-sm"></font-awesome-icon>
          </p>
          <p v-else class="text-lg font-bold text-green-700 flex items-center space-x-2">
            <font-awesome-layers class="text-xl">
              <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-medium-blue" />
              <font-awesome-icon :icon="['fas', 'plug']" class="text-xs text-brand-blue" :class="'text-brand-blue'" />
            </font-awesome-layers>
          </p>
          <span> Test Mode </span>
        </div>
        <div class="grid gap-4 max-w-xl mt-2">
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">Site name</p>
            <el-input v-if="!editTest && testCredentials.site" :value="testCredentials.site" type="text" disabled>
              <template slot="prepend">https://</template>
              <template slot="append">.chargebee.com</template>
            </el-input>
            <el-input v-else v-model="testSite" type="text" placeholder="your-company-test">
              <template slot="prepend">https://</template>
              <template slot="append">.chargebee.com</template>
            </el-input>
          </div>
          <div>
            <p class="text-sm font-semibold text-gray-700 mb-2">API Key</p>
            <el-input v-if="!editTest && testCredentials.apiKey" :value="testCredentials.apiKey.slice(0, 8) + '...'" type="text" disabled></el-input>
            <el-input v-else v-model="testApiKey" type="text" placeholder="test_YOUR_API_KEY"></el-input>
          </div>
        </div>
        <div class="mt-4">
          <el-button v-if="hasAllTestCredentials && !editTest" @click="editTest = true" type="primary">Edit Test Credentials</el-button>
          <el-button v-if="editTest" @click="editTest = false" type="text">Cancel</el-button>
          <el-button v-if="!hasAllTestCredentials || editTest" @click="saveTestCredentials" type="primary">Save</el-button>
          <el-button v-if="!editTest && hasAllTestCredentials" @click="checkTestMode" :loading="isLoadingTest" type="default"
            >Verify Connection</el-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrgService from '@/api/org.service';
import { SYNC_ORG } from '@/store/action-types';
import { CHARGEBEE_WRONG_VERSION_ERROR_CODE } from '@/store/shared';

export default {
  name: 'ChargebeeConnect',
  data() {
    return {
      loading: true,
      isLoadingLive: false,
      isLoadingTest: false,
      // allow editing credentials
      editLive: false,
      editTest: false,
      editVersion: false,
      // for inputs
      testSite: '',
      testApiKey: '',
      liveSite: '',
      liveApiKey: '',
      testValidated: false,
      liveValidated: false,
      liveBadCreds: false,
      testBadCreds: false,
      versionError: false,
      otherVersion: '',
      versionOptions: [
        {
          value: '1',
          label: 'Product Catalog 1.0',
        },
        {
          value: '2',
          label: 'Product Catalog 2.0',
        },
      ],
    };
  },
  async created() {
    try {
      await OrgService.testChargebeeConnect(this.user.org, 'test');
      this.testValidated = true;
    } catch (error) {
      this.testValidated = false;
      if (this.hasAllTestCredentials) {
        this.testBadCreds = true;
      }
      this.catchVersionError(error);
    }
    try {
      await OrgService.testChargebeeConnect(this.user.org, 'live');
      this.liveValidated = true;
    } catch (error) {
      this.liveValidated = false;
      if (this.hasAllLiveCredentials) {
        this.liveBadCreds = true;
      }
      this.catchVersionError(error);
    }
    this.loading = false;
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    org() {
      return this.$store.state.org;
    },
    version: {
      get() {
        return this.versionOptions.find((opt) => opt.value === this.org?.chargebeeVersion) || '2';
      },
      set(v) {
        this.org.chargebeeVersion = v.value;
      },
    },
    testCredentials() {
      if (this.org?.secrets?.test) {
        return {
          site: this.org.secrets.test.chargebeeSite,
          apiKey: this.org.secrets.test.chargebeeApiKey,
        };
      }
      return {};
    },
    liveCredentials() {
      if (this.org?.secrets?.prod) {
        return {
          site: this.org.secrets.prod.chargebeeSite,
          apiKey: this.org.secrets.prod.chargebeeApiKey,
        };
      }
      return {};
    },
    editedLiveCredentials() {
      return this.liveSite || this.liveApiKey;
    },
    editedTestCredentials() {
      return this.testSite || this.testApiKey;
    },
    hasAllLiveCredentials() {
      return this.liveCredentials.site && this.liveCredentials.apiKey;
    },
    hasAllTestCredentials() {
      return this.testCredentials.site && this.testCredentials.apiKey;
    },
  },
  methods: {
    deactivate() {
      this.$notify({ title: 'Deactivation Queued', type: 'warning' });
    },
    saveLiveCredentials() {
      this.saveChargebeeSecrets('live');
    },
    saveTestCredentials() {
      this.saveChargebeeSecrets('test');
    },
    saveVersion() {
      this.saveChargebeeVersion();
    },
    switchVersion() {
      const newOption = this.versionOptions.find((opt) => opt.value === this.otherVersion);
      this.version = newOption;
      this.saveChargebeeVersion();
    },
    async saveChargebeeVersion() {
      try {
        this.loading = true;
        await OrgService.saveChargebeeVersion(this.user.org, this.version.value);
        this.$notify({ title: 'Chargebee Product Catalog Version Saved', type: 'success' });
        await this.$store.dispatch(SYNC_ORG);
        this.editVersion = false;
      } catch (error) {
        console.log('Error', error);
        this.$notify({ title: 'Chargebee Product Catalog Version Update Failed', type: 'error' });
      } finally {
        if (this.hasAllTestCredentials) await this.checkTestMode();
        if (this.hasAllLiveCredentials) await this.checkLiveMode();
        this.loading = false;
      }
    },
    async saveChargebeeSecrets(mode) {
      try {
        const secrets = {};
        this.loading = true;
        const liveSite = this.liveSite || this.liveCredentials.site;
        if (liveSite && mode === 'live') {
          secrets.prod = {
            chargebeeSite: liveSite,
            chargebeeApiKey: this.liveApiKey,
          };
        }
        const testSite = this.testSite || this.testCredentials.site;
        if (testSite && mode === 'test') {
          secrets.test = {
            chargebeeSite: testSite,
            chargebeeApiKey: this.testApiKey,
          };
        }
        const result = await OrgService.saveChargebeeSecrets(this.user.org, secrets);
        this.$notify({ title: 'Chargebee Credentials Saved', type: 'success' });
        await this.$store.dispatch(SYNC_ORG);
        if (mode === 'live') {
          this.editLive = false;
        } else {
          this.editTest = false;
        }
      } catch (error) {
        console.log('Error', error);
        this.$notify({ title: 'Chargebee Credentials Update Failed', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    catchVersionError(error) {
      if (error.response?.data?.api_error_code === CHARGEBEE_WRONG_VERSION_ERROR_CODE) {
        this.otherVersion = this.version.value === '1' ? '2' : '1';
        this.versionError = this.version.value;
      }
    },
    async checkTestMode() {
      this.isLoadingTest = true;
      try {
        await OrgService.testChargebeeConnect(this.user.org, 'test');
        this.testValidated = true;
        this.testBadCreds = false;
        this.versionError = false;
        this.$notify({ title: 'Test Mode Connected', type: 'success' });
      } catch (error) {
        this.$notify({ title: 'Error', message: 'Chargebee Test Connection Failed', type: 'error' });
        this.testValidated = false;
        this.testBadCreds = true;
        this.catchVersionError(error);
        console.log('Error', error);
      }
      this.isLoadingTest = false;
    },
    async checkLiveMode() {
      this.isLoadingLive = true;
      try {
        const result = await OrgService.testChargebeeConnect(this.user.org, 'live');
        // console.log('result', result);
        this.liveValidated = true;
        this.liveBadCreds = false;
        this.versionError = false;
        this.$notify({ title: 'Live Mode Connected', type: 'success' });
      } catch (error) {
        this.$notify({ title: 'Error', message: 'Chargebee Live Connection Failed', type: 'error' });
        this.liveValidated = false;
        this.liveBadCreds = true;
        this.catchVersionError(error);
        console.log('Error', error, error.response);
      }
      this.isLoadingLive = false;
    },
  },
};
</script>
